/**
 * DateRangeSelector Component
 * Wrapper for DateRangeSelectionModal matching R0006 implementation pattern
 *
 * @module MetricDataDevTool/components/DateRangeSelector
 */

import React, { useState } from 'react';
import { Button, Box, Typography } from '@mui/material';
import { CalendarToday as CalendarIcon } from '@mui/icons-material';
import DateRangeSelectionModal from 'components/ReportHeader/modals/DateRangeSelectionModal';
import { format } from 'date-fns';

/**
 * DateRangeSelector component
 * @param {Object} props - Component props
 * @param {Object} props.dateRange - Current date range {from: Date, to: Date}
 * @param {Function} props.onChange - Callback when date range changes
 * @param {boolean} [props.disabled=false] - Whether the selector is disabled
 * @param {string} [props.error] - Error message to display
 */
export default function DateRangeSelector({
  dateRange,
  onChange,
  disabled = false,
  error = null
}) {
  const [modalOpen, setModalOpen] = useState(false);

  const handleOpenModal = () => {
    if (!disabled) {
      setModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleSubmit = (newDateRange) => {
    onChange(newDateRange);
    setModalOpen(false);
  };

  const formatDateRange = () => {
    if (!dateRange?.from || !dateRange?.to) {
      return 'Select Date Range';
    }

    const fromStr = format(dateRange.from, 'MMM d, yyyy');
    const toStr = format(dateRange.to, 'MMM d, yyyy');

    return `${fromStr} - ${toStr}`;
  };

  return (
    <Box>
      <Button
        variant="outlined"
        startIcon={<CalendarIcon />}
        onClick={handleOpenModal}
        disabled={disabled}
        fullWidth
        sx={{
          height: '40px',
          justifyContent: 'flex-start',
          textAlign: 'left',
          borderColor: error ? 'error.main' : '#7C3AED',
          borderWidth: '2px',
          color: '#7C3AED',
          '&:hover': {
            borderColor: error ? 'error.main' : '#6D28D9',
            borderWidth: '2px',
            backgroundColor: 'rgba(124, 58, 237, 0.04)'
          },
          '& .MuiButton-startIcon': {
            color: '#7C3AED'
          }
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: dateRange?.from && dateRange?.to ? 'text.primary' : 'text.secondary'
          }}
        >
          {formatDateRange()}
        </Typography>
      </Button>

      {error && (
        <Typography
          variant="caption"
          color="error"
          sx={{ mt: 0.5, ml: 1.75, display: 'block' }}
        >
          {error}
        </Typography>
      )}

      <DateRangeSelectionModal
        open={modalOpen}
        onClose={handleCloseModal}
        currentDateRange={dateRange}
        onSubmit={handleSubmit}
        minDate={new Date('2024-01-01')}
        maxDate={new Date()}
      />
    </Box>
  );
}
